import React, { useEffect, useState, useMemo } from 'react';
import FilterButton from '../../components/FilterButton';
import { useTranslate } from '../../components/TranslationText';
import LibraryFilter from '../../components/LibraryFilter';
import { filterResponsive, pageTitle, rightResponsive } from '../../common/cssclasses';
import LibraryCard from '../../components/LibraryCard';
import ActionButton from '../../components/ActionButton';
import useUmbracoQuery from '../../hooks/useUmbracoQuery';
import axiosInstance from '../../axios/axiosUmbraco';
import { formatUmbracoYear } from '../../helper/formatUmbracoDate';
import { formatDateArray } from '../../helper/formatDateArray';
import Loader from '../../components/Loader';

const fetchId = process.env.REACT_APP_UMBRACO_EAHS_LIBRARY;
const baseURL = process.env.REACT_APP_UMBRACO;
const MediaBaseURL = process.env.REACT_APP_UMBRACO_MEDIA_BASE_URL;

export default function LibraryPage() {
    const { translate } = useTranslate();

    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [showFilter, setShowFilter] = useState(false);
    const coursePerPage = 12;

    const [apiFilters, setApiFilters] = useState({});
    const [categories, setCategories] = useState([]);

    const queryFetchId = fetchId?.startsWith('children:') ? fetchId : `children:${fetchId}`;

    const {
        data: libraryRaw,
        isLoading,
        hasMore,
        fetchNextPage
    } = useUmbracoQuery(queryFetchId, apiFilters, coursePerPage, true, '');

    const filter = (filters) => {
        const newApiFilters = {};

        if (filters.title) {
            newApiFilters['name'] = filters.title;
        } else {
            newApiFilters['name'] = '';
        }

        if (filters.category) {
            newApiFilters['serviceCategoryName'] = filters.category;
        } else {
            newApiFilters['serviceCategoryName'] = '';
        }

        if (filters.DateFrom) {
            newApiFilters['publishDateFrom'] = formatUmbracoYear(filters.DateFrom);
        } else {
            newApiFilters['publishDateFrom'] = '';
        }

        if (filters.DateTo) {
            newApiFilters['publishDateTo'] = formatUmbracoYear(filters.DateTo);
        } else {
            newApiFilters['publishDateTo'] = '';
        }

        setApiFilters(newApiFilters);
    };

    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };
        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    // Fetch dynamic category list (Option B)
    useEffect(() => {
        async function fetchCategories() {
            try {
                const url = `/umbraco/delivery/api/v1/content?fetch=${queryFetchId}&skip=0&take=2000&fields=properties[category]`;
                const response = await axiosInstance.get(url, {
                    headers: {
                        "accept-language": localStorage.getItem("eahsLang") === "en" ? "en-US" : "ar-AE",
                    },
                });

                const rawItems = response?.data?.items || [];
                
                const categoriesList = rawItems.map(item => {
                    const catObj = item?.properties?.category;
                    if (catObj && catObj.items && catObj.items.length > 0) {
                        return catObj.items[0]?.content?.properties?.serviceCategoryName;
                    }
                    return null;
                });

                const uniqueCats = [...new Set(categoriesList)]
                    .filter(cat => cat !== undefined && cat !== null && cat !== '')
                    .sort();
                
                const formattedCats = uniqueCats.map((cat) => ({
                    text: cat,
                    label: cat,
                    value: cat,
                }));
                
                setCategories(formattedCats);
            } catch (e) {
                console.error("Failed to fetch library categories", e);
            }
        }
        fetchCategories();
    }, []);

    const displayedCourses = useMemo(() => {
        return libraryRaw.map((x) => {
            let extractedCategory = '';
            if (x?.properties?.category?.items?.length > 0) {
                // If there are multiple, join them, or just take the first. The old code mapped them into an array.
                // It returned an array of strings. We'll return an array of strings.
                extractedCategory = x.properties.category.items.map((cat) => (
                    cat?.content?.properties?.serviceCategoryName
                ));
            } else {
                extractedCategory = [];
            }

            return {
                title: x?.properties?.title ?? '',
                subtitle: x?.properties?.subtitle ?? '',
                dipslayDate: formatDateArray(x?.properties?.publishDate),
                publishDate: x?.properties?.publishDate,
                image: x?.properties?.image?.length > 0 ? MediaBaseURL + x?.properties?.image[0].url : '',
                content: (Array.isArray(x?.properties?.content) && x?.properties?.content?.length > 0)
                    ? baseURL + x?.properties?.content[0].url
                    : x?.properties?.content?.url
                        ? baseURL + x?.properties?.content.url
                        : '',
                category: extractedCategory
            };
        });
    }, [libraryRaw]);

    return (
        <>
            <div className="row rtl ">
                <h2 className={pageTitle}>
                    {translate('web.eahsLibrary', 'EAHS Library')}
                </h2>
            </div>
            <div className="row ">
                {windowWidth <= 970 && (
                    <div className="col-md-4 col-sm-6 col-6 text-center d-flex justify-content-center mb-3 mx-auto">
                        <FilterButton onClick={() => { setShowFilter(!showFilter) }} />
                    </div>
                )}
                <div className={filterResponsive}
                    style={{
                        display: windowWidth >= 768 ? 'block' : showFilter ? 'block' : 'none',
                    }}>
                    <LibraryFilter onSubmit={filter} categories={categories} />
                </div>

                <div className={rightResponsive}>
                    {isLoading && displayedCourses.length === 0 && (
                        <div className="text-center w-100 mt-5">
                            <i className="fas fa-spinner fa-spin fa-3x color-eahsred"></i>
                        </div>
                    )}
                    
                    {!isLoading && displayedCourses.length === 0 && (
                        <div className="text-center w-100 mt-5 bold color-eahsred">
                            {translate('web.nodataFound', 'No Albums Found with Your Filter Parameters')}
                        </div>
                    )}

                    <div className="row rtl">
                        {displayedCourses.length > 0 && displayedCourses.map((item, index) => (
                            <LibraryCard index={index} key={index} item={item} />
                        ))}
                    </div>

                    {/* Load More button */}
                    {hasMore && displayedCourses.length > 0 && (
                        <div className="d-flex justify-content-center align-items-center mt-3">
                            <ActionButton
                                label={translate('web.showMore', 'Show More')}
                                type={'loadmore'}
                                onClick={fetchNextPage}
                                disabled={isLoading}
                            />
                        </div>
                    )}
                </div>
            </div>
        </>
    )
}

